import { useContext } from "react";
import CustomBreadcrumbs from "../../components/minimal/custom-breadcrumbs";
import { fDate } from "../../utils/minimal/formatTime";
import { Stack } from "@mui/material";
import Label from "../../components/minimal/label";
import { useLocales } from "../../locales";
import useCopyToClipboard from "../../hooks/minimal/useCopyToClipboard";
import { useSnackbar } from "notistack";
import { useEventLink } from "../../hooks/useEventLink";
import Iconify from "../../components/minimal/iconify";
import { AuthContext } from "../../context-state/AuthContext";

export const EventBreadcrumbs = ({ heading, event, action, sellerName, sx = {} }) => {

    const { translate } = useLocales()
    const { enqueueSnackbar } = useSnackbar();
    const { copy } = useCopyToClipboard();
    const eventUrl = useEventLink()
    // Read auth context defensively (mock/demo pages render without an AuthProvider).
    const authContext = useContext(AuthContext)
    // The seller is the primary (non-sub) account, not the chosen sub-account.
    const resolvedSellerName = sellerName ?? authContext?.getPrimaryAccount?.()?.name
    // this domain + /scanner/ + event.id
    const scannerUrl = `${window.location.origin}/scanner/${event._id}`

    const handleCopy = (text) => {
        enqueueSnackbar(translate('dashboard.events.copied'));
        copy(text);
    };

    // Keep the breadcrumb trail on a single line so the date never drops below on
    // mobile: never wrap, ellipsis-truncate crumbs, and prioritise which shrinks —
    // the seller (first) yields space first, the date (last) is never truncated,
    // so the event name stays readable.
    const breadcrumbsSx = {
        '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
        '& .MuiBreadcrumbs-li': { minWidth: 0 },
        '& .MuiBreadcrumbs-li:first-of-type': { flexShrink: 20 },
        '& .MuiBreadcrumbs-li:first-of-type > *': { maxWidth: { xs: 90, sm: 150 } },
        // Event crumb (the middle one) — cap on mobile only so it truncates too.
        '& .MuiBreadcrumbs-li:not(:first-of-type):not(:last-of-type) > *': { maxWidth: { xs: 130, sm: 'none' } },
        '& .MuiBreadcrumbs-li:last-of-type': { flexShrink: 0 },
        '& .MuiBreadcrumbs-li > *': {
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        ...sx,
    };

    return (
        <Stack>
            <CustomBreadcrumbs
                action={action}
                sx={breadcrumbsSx}
                heading={heading}
                headAction={
                    <Stack direction="row" gap="10px" alignItems="center">
                        <Stack direction="row" gap="5px">
                            <Label onClick={() => handleCopy(eventUrl)} sx={{ py: 1, mt: 1, cursor: "pointer" }} color="info" variant="soft">
                                {translate('dashboard.events.copy_event_link')}
                            </Label>
                            <Label
                                onClick={() => window.open(eventUrl, '_blank')}
                                sx={{ py: 1, mt: 1, cursor: "pointer", minWidth: 'auto', px: 1 }}
                                color="info"
                                variant="soft"
                                title={translate('dashboard.events.open_in_new_tab')}
                            >
                                <Iconify icon="mdi:open-in-new" width={18} />
                            </Label>
                        </Stack>
                        <Stack direction="row" gap="5px">
                            <Label onClick={() => handleCopy(scannerUrl)} sx={{ py: 1, mt: 1, cursor: "pointer" }} color="warning" variant="soft">
                                {translate('dashboard.events.copy_scanner_link')}
                            </Label>
                            <Label
                                onClick={() => window.open(scannerUrl, '_blank')}
                                sx={{ py: 1, mt: 1, cursor: "pointer", minWidth: 'auto', px: 1 }}
                                color="warning"
                                variant="soft"
                                title={translate('dashboard.events.open_in_new_tab')}
                            >
                                <Iconify icon="mdi:open-in-new" width={18} />
                            </Label>
                        </Stack>
                    </Stack>
                }
                links={[
                    ...(resolvedSellerName ? [{ name: resolvedSellerName }] : []),
                    {
                        name: event.name,
                    },
                    {
                        name: fDate(event.start_date),
                    }
                ]}
            />
        </Stack>
    )
}